import React, { useState } from 'react';
import { motion } from 'framer-motion';
import founderImg from '../assets/Til Babuji.jpeg';

const About = ({ setCurrentPage }) => {
  const [activeTimeline, setActiveTimeline] = useState(0);

  const timelineItems = [
    {
      year: "1990",
      title: "Foundation",
      description: "Samyak Decor was established with a vision to transform living spaces into artistic masterpieces."
    },
    {
      year: "2001",
      title: "Expansion",
      description: "Expanded services to include commercial and institutional projects, establishing our reputation in the market."
    },
    {
      year: "Present",
      title: "Innovation",
      description: "Continuing to innovate with sustainable materials and cutting-edge design techniques."
    }
  ];

  return (
    <section id="about" className="py-20 relative overflow-hidden bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900">
      {/* Subtle architectural pattern background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.03)_0%,rgba(0,0,0,0.2)_100%)]"></div>

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-6"
          >
            About <span className="text-yellow-400">Samyak Decor</span>
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: 100 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 w-32 mx-auto mb-6 rounded-full"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg sm:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed px-4 mb-8"
          >
            For over three decades, Samyak Decor has been redefining spaces with a perfect balance of craftsmanship, creativity, and innovation.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex justify-center"
          >
            <button
              onClick={() => setCurrentPage('about-us')}
              className="bg-[#d4af37] hover:bg-[#c9a227] text-white font-bold py-3 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              View More
            </button>
          </motion.div>
        </div>

        {/* Two-Column Layout: Our Heritage and Director Card */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12 mb-16 sm:mb-20">
          {/* Our Heritage - Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="group relative overflow-hidden rounded-3xl border-2 border-yellow-500 bg-black/40 backdrop-blur-xl transition-all duration-700 hover:shadow-2xl shadow-xl hover:shadow-yellow-500/20"
            style={{
              border: '2px solid #EAB308',
            }}
          >
            {/* Gradient glow effect on hover */}
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400/20 via-transparent to-yellow-400/20 rounded-3xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

            <div className="p-6 sm:p-10 h-full flex flex-col z-10">
              <h3 className="text-2xl sm:text-3xl font-bold text-yellow-400 mb-6 sm:mb-8 text-center">Our Heritage</h3>
              <div className="space-y-6 text-justify">
                <p className="text-gray-200 leading-relaxed text-base sm:text-lg">
                  For over three decades, Samyak Decor has been redefining spaces with a perfect balance of craftsmanship, creativity, and innovation.
                </p>
                <p className="text-gray-200 leading-relaxed text-base sm:text-lg">
                  Founded in 1990, our journey began with a simple yet powerful vision — to transform ordinary spaces into meaningful environments that reflect the personality, purpose, and aspirations of those who inhabit them.
                </p>
              </div>
            </div>

            {/* Enhanced Glassy Hover Effects */}
            <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br from-yellow-400/10 to-transparent pointer-events-none"></div>
          </motion.div>

          {/* Director Profile - Right Column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="group relative overflow-hidden rounded-3xl border-2 border-yellow-500 bg-black/40 backdrop-blur-xl transition-all duration-700 hover:shadow-2xl shadow-xl hover:shadow-yellow-500/20"
            style={{
              border: '2px solid #EAB308',
            }}
          >
            {/* Gradient glow effect on hover */}
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400/20 via-transparent to-yellow-400/20 rounded-3xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

            <div className="p-6 sm:p-10 text-center h-full flex flex-col justify-center z-10">
              <div className="relative w-48 h-48 mx-auto rounded-full overflow-hidden mb-6 group border-4 border-yellow-500 shadow-lg">
                <img
                  src={founderImg}
                  alt="Trilokchand Suthar - Founder & Managing Director"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 mb-2">Trilokchand Suthar</h3>
                <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-4">Founder & Managing Director</p>
                <div className="inline-block px-6 py-3 bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 rounded-lg border border-yellow-500/30 backdrop-blur-sm">
                  <span className="text-yellow-400 font-bold text-lg">Experience since 1990</span>
                </div>
              </div>
            </div>

            {/* Enhanced Glassy Hover Effects */}
            <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br from-yellow-400/10 to-transparent pointer-events-none"></div>
          </motion.div>
        </div>

        {/* Timeline Section - Redesigned & Responsive */}
        <div className="mb-16 container mx-auto px-4 relative">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl font-bold text-center text-yellow-400 mb-12 sm:mb-20"
          >
            Our Journey
          </motion.h3>

          <div className="relative">
            {/* Center Decorative Line (Desktop Only) */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-[2px] bg-gradient-to-b from-transparent via-yellow-500/30 to-transparent hidden md:block"></div>

            <div className="space-y-12 md:space-y-24">
              {timelineItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onMouseEnter={() => setActiveTimeline(index)}
                  className={`relative flex flex-col items-center justify-between gap-6 md:gap-0 ${index % 2 === 0 ? 'md:flex-row-reverse' : 'md:flex-row'
                    }`}
                >
                  {/* PUSH ELEMENT (Desktop Spacer) - Keeps the layout balanced */}
                  <div className="hidden md:block md:w-[45%]"></div>

                  {/* TIMELINE CIRCLE */}
                  <div className="relative z-30 flex-shrink-0">
                    <div className="w-16 h-16 md:w-24 md:h-24 aspect-square rounded-full bg-neutral-900 border-2 border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.4)] flex items-center justify-center group hover:scale-110 transition-transform duration-500 relative">
                      {/* Inner Ring (Static) */}
                      <div className="absolute inset-1 rounded-full border border-yellow-500/30"></div>
                      <span className="text-yellow-400 font-bold text-sm md:text-xl">{item.year}</span>
                    </div>
                  </div>

                  {/* CONTENT CARD */}
                  <div className="w-full md:w-[45%] flex justify-center px-4 md:px-0">
                    <motion.div
                      className={`relative overflow-hidden rounded-2xl border-[3px] border-yellow-500 bg-gradient-to-br from-[#1a1a1a] to-black backdrop-blur-md p-6 md:p-8 w-full mx-auto shadow-lg hover:shadow-[0_0_20px_rgba(234,179,8,0.15)] transition-all duration-300 group ${activeTimeline === index ? 'scale-[1.02] shadow-[0_0_20px_rgba(234,179,8,0.3)]' : ''
                        }`}
                    >
                      {/* Decorative Gloss Gradient */}
                      <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 bg-yellow-500/10 blur-3xl rounded-full group-hover:bg-yellow-500/20 transition-all duration-500"></div>

                      <div className="relative z-10">
                        <h4 className="text-xl md:text-2xl font-bold text-yellow-400 mb-3 text-center md:text-left">{item.title}</h4>
                        <p className="text-gray-300 leading-relaxed text-sm md:text-base text-center md:text-left font-light">{item.description}</p>
                      </div>
                    </motion.div>
                  </div>

                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;